import type { ReactNode } from "react";
import type { TableMeta } from "@tanstack/react-table";
import type { Estimation } from "@/interfaces/estimation.interface";
import { DataTable } from "@/components/ui/data-table";
import { Empty } from "@/components/dashboard/Empty";
import { RowSkeleton } from "@/components/skeleton/RowSkeleton";
import { StatusSkeleton } from "@/components/skeleton/StatusSkeleton";
import { UserSkeleton } from "@/components/skeleton/UserSkeleton.";
import { FieldDataSkeleton } from "@/components/skeleton/FieldDataSkeleton";
import { columns } from "./columns";

function renderSkeleton(columnId: string): ReactNode {
  switch (columnId) {
    case "status":
      return <StatusSkeleton />;
    case "assignee":
    case "createdBy":
      return <UserSkeleton />;
    case "fieldData":
      return <FieldDataSkeleton />;
    default:
      return <RowSkeleton />;
  }
}

interface DashboardTableProps {
  data: Estimation[];
  isLoading: boolean;
  pageSize: number;
  onClickRow: (id: string) => void;
  meta?: TableMeta<Estimation>;
  page?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function DashboardTable({
  data,
  isLoading,
  pageSize,
  onClickRow,
  meta,
  page,
  total,
  onPageChange,
  onPageSizeChange,
}: DashboardTableProps) {
  const isServerPaginated = page !== undefined && total !== undefined;
  return (
    <DataTable<Estimation>
      columns={columns}
      data={data}
      isLoading={isLoading}
      pageSize={pageSize}
      onClickRow={onClickRow}
      meta={meta}
      renderSkeleton={renderSkeleton}
      emptyState={<Empty />}
      manualPagination={isServerPaginated}
      pageCount={isServerPaginated ? Math.ceil(total / pageSize) : undefined}
      totalRows={isServerPaginated ? total : undefined}
      pageIndex={isServerPaginated ? page - 1 : undefined}
      onPageChange={isServerPaginated && onPageChange ? (pageIndex) => onPageChange(pageIndex + 1) : undefined}
      onPageSizeChange={onPageSizeChange}
    />
  );
}
